/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable react/no-deprecated */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import { Button } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'fit-content',
  minWidth: '32rem',
  minHeight: '18rem',
  bgcolor: 'background.paper',
  border: 'none',
  boxShadow: 24,
  p: 4,
  borderRadius: '1.5rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
};

interface ConfirmationModelProps {
  open: boolean;
  handleCancel: () => void;
  handleConfirm: () => void;
  message: string;
  Title?: string;
}

const ConfirmationModel: React.FC<ConfirmationModelProps> = ({
  open,
  handleCancel,
  handleConfirm,
  message,
  Title,
}) => {
  const { t } = useTranslation('stimulation_config');
  return (
    <Modal
      open={open}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        {/* Centered warning icon in a circle */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '5rem',
          height: '5rem',
          borderRadius: '50%',
          background: '#FFF3ED',
          marginBottom: 2,
        }}>
          <WarningAmberIcon sx={{ color: '#E17E61', fontSize: '2.5rem' }} />
        </Box>
        {/* Title */}
        <Typography
          fontFamily={'nunito'}
          id="modal-modal-title"
          variant="h5"
          component="p"
          color="#222"
          fontWeight={700}
          sx={{ textAlign: 'center', mb: 1 }}
        >
          {Title}
        </Typography>
        <Typography
          fontFamily={'nunito'}
          id="modal-modal-description"
          variant="h6"
          component="p"
          color="#222"
          fontWeight={400}
          sx={{ textAlign: 'center', mb: 3, fontSize: '1.25rem' }}
        >
          {message}
        </Typography>
        {/* Buttons side by side */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', width: '100%' }}>
          <Button
            sx={{
              color: '#222',
              border: '2px solid #222',
              borderRadius: '0.75rem',
              minWidth: '8rem',
              fontWeight: 600,
              fontSize: '1.1rem',
              background: 'white',
              boxShadow: 'none',
              '&:hover': { background: '#f5f5f5', borderColor: '#222' },
            }}
            variant="outlined"
            onClick={handleCancel}
          >
            {t('No')}
          </Button>
          <Button
            sx={{
              background: '#222',
              color: 'white',
              borderRadius: '0.75rem',
              minWidth: '8rem',
              fontWeight: 600,
              fontSize: '1.1rem',
              boxShadow: 'none',
              '&:hover': { background: '#444' },
            }}
            variant="contained"
            onClick={handleConfirm}
          >
            {t('Yes')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

let modalRoot = document.getElementById('modal-root');

if (!modalRoot) {
  modalRoot = document.createElement('div');
  modalRoot.setAttribute('id', 'modal-root');
  document.body.appendChild(modalRoot);
}

export const showConfirmationModal = ({
  message,
  onConfirm,
  onCancel,
  Title,
}: {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  Title?: string;
}) => {
  const handleConfirm = () => {
    onConfirm();
    cleanup();
  };

  const handleCancel = () => {
    onCancel();
    cleanup();
  };

  const cleanup = () => {
    ReactDOM.unmountComponentAtNode(modalRoot!);
  };

  ReactDOM.render(
    <ConfirmationModel
      open={true}
      handleCancel={handleCancel}
      handleConfirm={handleConfirm}
      message={message}
      Title={Title}
    />,
    modalRoot
  );
};